import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { attentionItems, type AttentionItem, type DraftWithError } from '@/lib/attention'
import { useDeskHealth, useDrafts, useSchedule, useTargets } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { errorText } from './ErrorState'
import { StatusDot } from './StatusPill'
import { When } from './When'

const PUBLISHING = { status: 'publishing' } as const

/** The band under every page header that lists what is broken right now:
 *  desk jobs failing, posts that failed to go up, codes that did not send,
 *  requests stuck in the queue. When nothing is wrong it is not drawn at all.
 *
 *  Fed by GET /api/redeem/health, the failed campaign drafts and the posts
 *  the schedule has in "publishing" — all polled every 30 seconds through the
 *  shared query cache — and the accounts' token expiry. */
export function AttentionStrip({ className }: { className?: string }) {
  return <AttentionList items={useAttentionItems()} className={className} />
}

/** The strip's items, for a page that shows them its own way (오늘 folds
 *  them into its "확인할 것" list instead of drawing the strip). */
export function useAttentionItems(): AttentionItem[] {
  const health = useDeskHealth()
  const failed = useDrafts('failed', {}, true)
  const publishing = useSchedule(PUBLISHING, { live: true })
  const targets = useTargets()
  return useMemo(() => {
    const list = attentionItems(health.data, failed.data?.items as DraftWithError[] | undefined, {
      targets: targets.data,
      publishing: publishing.data?.items,
    })
    if (health.error) {
      list.unshift({
        key: 'health-error',
        tone: 'failed',
        label: '데스크 상태를 읽지 못했다',
        count: 0,
        to: '/redeem',
        details: [{ where: 'GET /api/redeem/health', message: errorText(health.error) }],
      })
    }
    return list
  }, [health.data, health.error, failed.data, targets.data, publishing.data])
}

/** The strip's drawing, apart from its data — tests render this directly. */
export function AttentionList({ items, className }: { items: AttentionItem[]; className?: string }) {
  const [open, setOpen] = useState(false)
  if (items.length === 0) return null
  const severe = items.some((i) => i.tone === 'failed')
  return (
    <section
      aria-label="확인할 것"
      className={cn('relative overflow-hidden rounded-lg border border-border bg-card pl-1', className)}
    >
      <span
        aria-hidden
        className={cn('absolute inset-y-0 left-0 w-1', severe ? 'bg-status-failed' : 'bg-status-publishing')}
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2">
        <h2
          className={cn(
            'order-1 flex shrink-0 items-center gap-2 text-table font-semibold',
            severe ? 'text-status-failed' : 'text-status-publishing',
          )}
        >
          <StatusDot tone={severe ? 'failed' : 'publishing'} />
          <span>
            확인할 것 <span className="tabular-nums">{items.length}</span>건
          </span>
        </h2>
        {/* One line on the phone, sideways if it has to: the strip is on every
            page and should not push the page down. */}
        <ul className="order-3 -mx-3 flex w-[calc(100%+1.5rem)] min-w-0 gap-1.5 overflow-x-auto px-3 [scrollbar-width:none] md:order-2 md:mx-0 md:w-auto md:flex-1 md:flex-wrap md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden">
          {items.map((i) => (
            <li key={i.key} className="shrink-0">
              <Link
                to={i.to}
                className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-background px-2.5 text-meta font-medium text-foreground transition-colors hover:border-foreground/25 hover:bg-muted"
              >
                <StatusDot tone={i.tone} />
                {i.label}
                {i.count > 0 && <span className="tabular-nums text-muted-foreground">{i.count}</span>}
              </Link>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="order-2 ml-auto inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-meta font-medium text-muted-foreground hover:bg-muted hover:text-foreground md:order-3"
        >
          {open ? '접기' : '자세히'}
          <ChevronDown size={14} className={cn('transition-transform', open && 'rotate-180')} aria-hidden />
        </button>
      </div>
      {open && (
        <div className="space-y-3 border-t border-border bg-muted/30 px-3 py-3">
          {items.map((i) => (
            <div key={i.key} className="space-y-1.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-table font-semibold">
                  {i.label}
                  {i.count > 0 && <span className="ml-1 tabular-nums text-muted-foreground">{i.count}</span>}
                </span>
                <Link to={i.to} className="text-meta font-medium text-accent hover:underline">
                  열기
                </Link>
              </div>
              <ul className="space-y-1.5">
                {i.details.map((d, n) => (
                  <li key={n} className="text-meta">
                    <div className="flex flex-wrap items-baseline gap-x-2 text-muted-foreground">
                      <span className="font-medium text-foreground">{d.where}</span>
                      {d.what && <span className="min-w-0 truncate">{d.what}</span>}
                      {d.at && <When iso={d.at} className="ml-auto shrink-0" />}
                    </div>
                    {d.message && (
                      <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-muted px-2 py-1 font-mono text-micro text-foreground">
                        {d.message}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
